/**
 * The button in the bottom-right corner that opens the assistant, as a help
 * bubble works on most professional sites. App pages only, signed-in people
 * only.
 *
 * Built to be noticed (owner, 2026-10-11: "someone may miss it"): brand blue
 * like the Export button, a labelled pill on larger screens, and until the
 * person has opened the assistant once, a soft pulse and a one-line invitation
 * that appears after a few seconds. Opening the assistant or closing the
 * invitation retires both for good.
 *
 * And it can be put away, as help widgets usually allow: a small × hides it
 * into a narrow tab on the screen's right edge, which brings it back. On the
 * planner the button can sit over the map's own corner controls (the shortcut
 * sheet), so this matters there most. The choice is remembered.
 *
 * On the phone planner the corner belongs to the "+" tools button, so the
 * bubble sits just above it and hides with it (the bottom sheet publishes
 * --mobile-fab-display while the keyboard is up). The bubble publishes its
 * height as --assistant-launcher-h, and the open window its width as
 * --assistant-panel-w, so toasts stay clear of both.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, MessageCircleQuestion, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAssistantStore } from './assistantStore';

const APP_PREFIXES = ['/dashboard', '/settings', '/organization', '/cctv-design', '/tenders', '/api-docs'];

/** The phone "+" button's bottom edge (MobileFAB's FAB_BOTTOM): the bubble stacks above it. */
const PHONE_FAB_BOTTOM = 'max(calc(24px + max(24px, env(safe-area-inset-bottom))), calc(var(--mobile-sheet-h, 0px) + 12px), calc(var(--mobile-panel-h, 0px) + 12px))';

/** On the desktop planner the button sits over the map, left of the open edit
 *  panel (which publishes its width) and above the map's corner controls. */
const DESIGNER_RIGHT = 'calc(var(--designer-edit-panel-w, 0px) + max(20px, env(safe-area-inset-right)))';
const DESIGNER_LIFT_PX = 56;

/** Set once the person has opened the assistant or closed the invitation. */
const INTRO_DONE_KEY = 'cctvp_assistant_intro_done';
/** Set while the person keeps the button tucked into its edge tab. */
const HIDDEN_KEY = 'cctvp_assistant_launcher_hidden';
const INVITE_DELAY_MS = 4000;

function bare(pathname: string): string {
  return pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/, '');
}

export function isAssistantPage(pathname: string): boolean {
  const p = bare(pathname);
  return APP_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
}

function readIntroDone(): boolean {
  try { return localStorage.getItem(INTRO_DONE_KEY) === '1'; } catch { return true; }
}

function writeIntroDone() {
  try { localStorage.setItem(INTRO_DONE_KEY, '1'); } catch { /* private mode: the invitation may show again */ }
}

function readHidden(): boolean {
  try { return localStorage.getItem(HIDDEN_KEY) === '1'; } catch { return false; }
}

function writeHidden(hidden: boolean) {
  try {
    if (hidden) localStorage.setItem(HIDDEN_KEY, '1');
    else localStorage.removeItem(HIDDEN_KEY);
  } catch { /* private mode: remembered for this page only */ }
}

const AssistantLauncher: React.FC = () => {
  const { t } = useTranslation(['common']);
  const { user } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const open = useAssistantStore((s) => s.open);
  const openPanel = useAssistantStore((s) => s.openPanel);
  const closePanel = useAssistantStore((s) => s.closePanel);
  const [introDone, setIntroDone] = useState(readIntroDone);
  const [inviting, setInviting] = useState(false);
  const [tucked, setTucked] = useState(readHidden);

  const p = bare(location.pathname);
  const onDesigner = p === '/cctv-design' || p.startsWith('/cctv-design/');
  const phoneDesigner = isMobile && onDesigner;
  const visible = !!user && isAssistantPage(location.pathname) && !(isMobile && open);
  // Tucked away: only the edge tab shows, unless the window is open anyway
  // (from the menu or a guide's way back), when the button is its close.
  const showButton = visible && (!tucked || open);

  useEffect(() => {
    const root = document.documentElement.style;
    // The button (56 px + gap) and, while it shows, the invitation above it.
    const lift = onDesigner && !isMobile ? DESIGNER_LIFT_PX : 0;
    if (showButton && !phoneDesigner) root.setProperty('--assistant-launcher-h', `${(inviting ? 176 : 72) + lift}px`);
    else root.removeProperty('--assistant-launcher-h');
    // The open window (400 px + its 20 px margin) on screens wide enough to float it.
    if (open && !isMobile) root.setProperty('--assistant-panel-w', '420px');
    else root.removeProperty('--assistant-panel-w');
    return () => {
      root.removeProperty('--assistant-launcher-h');
      root.removeProperty('--assistant-panel-w');
    };
  }, [showButton, phoneDesigner, onDesigner, open, isMobile, inviting]);

  // Opening the assistant by any route (bubble, menu, a guide's way back)
  // retires the introduction.
  useEffect(() => {
    if (open && !introDone) { writeIntroDone(); setIntroDone(true); setInviting(false); }
  }, [open, introDone]);

  // The invitation comes a few seconds after a page settles, not on top of it.
  // A phone's list pages have no room for it: there it lay over the projects,
  // so the pulsing button carries the invitation alone.
  useEffect(() => {
    if (!showButton || introDone || open || (isMobile && !onDesigner)) { setInviting(false); return; }
    const timer = window.setTimeout(() => setInviting(true), INVITE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [showButton, introDone, open, location.pathname, isMobile, onDesigner]);

  if (!visible) return null;

  const screen = onDesigner ? (isMobile ? 'designer-phone' : 'designer')
    : p.startsWith('/dashboard') ? 'dashboard'
    : p.startsWith('/settings') ? 'settings'
    : p.startsWith('/organization') ? 'organization'
    : 'other';
  const label = t('assistant_launcher', { defaultValue: 'Ask the assistant' });
  const pill = !isMobile && !open;
  const toggle = () => (open ? closePanel() : openPanel(screen));
  const dismissInvite = () => { writeIntroDone(); setIntroDone(true); setInviting(false); };
  const tuck = () => { writeHidden(true); setTucked(true); dismissInvite(); };
  const untuck = () => { writeHidden(false); setTucked(false); };

  const position: React.CSSProperties = phoneDesigner
    ? { right: 'max(20px, calc(env(safe-area-inset-right) + 4px))', bottom: `calc(${PHONE_FAB_BOTTOM} + 68px)`, display: 'var(--mobile-fab-display, flex)' }
    : onDesigner
      ? { right: DESIGNER_RIGHT, bottom: `max(${20 + DESIGNER_LIFT_PX}px, env(safe-area-inset-bottom))` }
      : { right: 'max(20px, env(safe-area-inset-right))', bottom: 'max(20px, env(safe-area-inset-bottom))' };

  if (!showButton) {
    // The edge tab: narrow, on the very edge and above the map's corner
    // controls, so it covers nothing.
    return (
      <button
        type="button"
        data-guide="assistant.launcher-tab"
        data-assistant-launcher=""
        onClick={untuck}
        aria-label={t('assistant_launcher_show', { defaultValue: 'Show the assistant' })}
        title={t('assistant_launcher_show', { defaultValue: 'Show the assistant' })}
        className="fixed right-0 z-[60] flex h-11 w-6 items-center justify-center rounded-l-md bg-[#5B8CFF] text-white shadow-md transition-[width] hover:w-8 hover:bg-[#4F7CE0] dark:text-graphite-900"
        style={phoneDesigner
          ? { bottom: `calc(${PHONE_FAB_BOTTOM} + 72px)`, display: 'var(--mobile-fab-display, flex)' }
          : { bottom: 'max(112px, calc(env(safe-area-inset-bottom) + 92px))', right: onDesigner ? 'var(--designer-edit-panel-w, 0px)' : 0 }}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div data-assistant-launcher="" className="fixed z-[60] flex flex-col items-end gap-2 pointer-events-none" style={position}>
      {inviting && (
        <div
          role="status"
          className="pointer-events-auto relative max-w-[260px] border border-gray-200 bg-white p-3 pr-8 text-left shadow-xl dark:border-brand-border-soft dark:bg-graphite-900 animate-scale-in"
        >
          <button
            type="button"
            onClick={dismissInvite}
            aria-label={t('close', { defaultValue: 'Close' })}
            className="absolute right-1.5 top-1.5 p-1 text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={toggle} className="block text-left">
            <span className="block text-sm font-semibold text-foreground dark:text-white">
              {t('assistant_invite_title', { defaultValue: 'Questions about the planner?' })}
            </span>
            <span className="mt-0.5 block text-xs leading-snug text-gray-600 dark:text-neutral-300">
              {t('assistant_invite_body', { defaultValue: 'Ask me anything. I will answer and show you where it is.' })}
            </span>
          </button>
        </div>
      )}
      <div className="group pointer-events-auto relative">
      <button
        type="button"
        data-guide="assistant.launcher"
        onClick={toggle}
        aria-label={open ? t('close', { defaultValue: 'Close' }) : label}
        aria-expanded={open}
        title={label}
        className={`pointer-events-auto relative flex items-center justify-center gap-2 rounded-full bg-[#5B8CFF] font-semibold text-white shadow-[0_8px_24px_-6px_rgba(91,140,255,0.65)] transition-transform hover:bg-[#4F7CE0] active:scale-95 dark:text-graphite-900 dark:hover:bg-[#7AABFF] ${
          pill ? 'h-14 pl-4 pr-5 text-sm' : phoneDesigner ? 'h-12 w-12' : 'h-14 w-14'
        }`}
      >
        {!introDone && !open && (
          <span aria-hidden className="absolute inset-0 rounded-full bg-[#5B8CFF] opacity-40 animate-ping" />
        )}
        {open
          ? <X className="relative h-6 w-6" />
          : <MessageCircleQuestion className="relative h-6 w-6 shrink-0" />}
        {pill && <span className="relative whitespace-nowrap">{label}</span>}
      </button>
      {!open && (
        // Put it away: on hover where there is a pointer, always on touch.
        <button
          type="button"
          onClick={tuck}
          aria-label={t('assistant_launcher_hide', { defaultValue: 'Hide the assistant button' })}
          title={t('assistant_launcher_hide', { defaultValue: 'Hide the assistant button' })}
          className="absolute -left-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full border border-white/20 bg-neutral-900 text-white opacity-0 shadow transition-opacity focus:opacity-100 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 dark:border-black/20 dark:bg-white dark:text-neutral-900"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      </div>
    </div>
  );
};

export default AssistantLauncher;
